import { AlertCircle } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';

/**
 * What the recruiter reads when their board could not be built.
 *
 * This used to be one line — `Could not load the board: Failed to build board`
 * — under an unconditional Retry button, because the proxy answered every
 * failure with that one string. A recruiter who had no Lever account and a
 * whole team locked out by an expired Lever key saw the identical screen, and
 * the only honest thing either could do was click Retry until someone came to
 * help. The August 2026 rollout stalled on exactly that.
 *
 * So the button is conditional. `retryable` comes from the typed `reason` the
 * proxy now sends (see `lib/boardError.js` and `BoardLoadError`), and a false
 * value means retrying is KNOWN to be futile — showing the button anyway would
 * be the same lie in a nicer font. The message is the proxy's own sentence; it
 * is not prefixed here, because a good message is already a full sentence and
 * "Could not load the board: You don't have a board yet" reads like a bug.
 */
export interface BoardErrorInfo {
  message: string;
  retryable: boolean;
}

export function BoardErrorState({
  error,
  onRetry,
}: {
  error: BoardErrorInfo | null;
  onRetry: () => void;
}) {
  // A null error with no board still has to say something — this is the
  // "resolved with nothing" case, which is a fault even without a thrown error.
  const message = error?.message ?? 'Could not load the board.';
  const retryable = error?.retryable ?? true;

  return (
    <div className="flex flex-col items-center gap-3 py-12 text-sm text-[#791F1F]">
      <AlertCircle className="h-5 w-5" />
      <div className="max-w-md text-center">{message}</div>
      {retryable && (
        <Button variant="outline" onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
}
